import { AgDialog, navBarOpenAtom } from '@australiangreens/ag-internal-components';
import { Box } from '@mui/material';
import { ImmutableTree } from '@react-awesome-query-builder/mui';
import { useAtom, useSetAtom } from 'jotai';
import { SearchTemplatePageContext } from '@/context/SearchTemplateContext';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router';

import { SearchTemplateMetadata } from '@/apis/searchtemplates';
import { NotRenewingContext } from '@/App';
import { InternalLogicError } from '@/errors/InternalLogicError';
import { useFeatureToggle } from '@/featureToggles/FeatureTogglesContext';
import { SearchTreeNode } from '@/types/searchTreeTypes';
import { compareElectionPlaceholders, hasNotRenewing } from '@/util';
import BlockerDialog from '@components/BlockerDialog';
import Sidebar from '@components/Sidebar';
import ToggleDebugButton from '@components/ToggleDebugButton';
import { useSentryTags } from '@hooks/useSentryTags';
import { useUnsavedChangesBlocker } from '@hooks/useUnsavedChangesBlocker';
import { debugAtom } from '@pages/AdvancedSearchListCreatorPage';
import useQueryBuilderConfig from '@/pages/AdvancedSearchListCreatorPage/queryBuilderConfig';
import {
  checkIsSearchTreeIncomplete,
  mapToSearchTree,
  SearchTreeBuilderState,
  SearchTreeChangeHandler,
} from '@/pages/AdvancedSearchListCreatorPage/SearchTreeBuilder';
import SearchTreeBuilderSection from './SearchTreeSection';
import TemplatePanelContent, { TemplatePanelContentHandle } from './TemplatePanelContent';
import { getHiddenPlaceholderKeys, templateNameToDisplay } from './util';

export { SearchTemplatePageContext } from '@/context/SearchTemplateContext';

export { SEARCH_TEMPLATE_SAVE_TOOLTIP_TEXT } from '@/constants/searchTemplate';

type AdvancedSearchListCreatorPageProps = {
  searchTemplateId: string | undefined;
  loadedTree: ImmutableTree;
  loadedMetadata: SearchTemplateMetadata | undefined;
};

export default function SearchTemplatePage({
  searchTemplateId,
  loadedTree,
  loadedMetadata,
}: AdvancedSearchListCreatorPageProps) {
  useSentryTags({ 'template-page': 'create' });
  const enableDevFeatures = useFeatureToggle('Developer Features');
  const [debug, setDebug] = useAtom(debugAtom);
  const setIsNavbarOpen = useSetAtom(navBarOpenAtom);
  const { setNotRenewingPresent } = useContext(NotRenewingContext);
  const [isSearchTreeIncomplete, setIsSearchTreeIncomplete] = useState(
    checkIsSearchTreeIncomplete(loadedTree)
  );
  const navigate = useNavigate();
  const [hasBeenModified, setHasBeenModified] = useState(false);
  const { blocker, popStateBlock, allowNextNavigation } = useUnsavedChangesBlocker(hasBeenModified);

  const handleTemplateSaveSuccess = useCallback(
    (targetPath: string) => {
      // Allow this navigation through useBlocker before hasBeenModified flushes (same as deferred effect).
      allowNextNavigation();
      setHasBeenModified(false);
      navigate(targetPath, { replace: true });
    },
    [allowNextNavigation, navigate]
  );

  // TODO: Move this state into SearchTreeSection, we don't need it here.
  // TODO: Challenge is searchTree depends on the builder state if we move the builder state down
  // TODO: we'll have to pass it back up since searchTree is needed by TemplatePanelContent.
  const searchTreeConfig = useQueryBuilderConfig({ isOnTemplatePage: true });
  const [searchTreeBuilderState, setSearchTreeBuilderState] = useState<SearchTreeBuilderState>({
    tree: loadedTree,
    config: searchTreeConfig,
  });

  const initialSearchTreeForBuilder = searchTreeBuilderState.tree
    ? mapToSearchTree(searchTreeBuilderState.tree, searchTreeBuilderState.config, {})
    : undefined;

  const [searchTree, setSearchTree] = useState<SearchTreeNode | undefined>(
    initialSearchTreeForBuilder
  );

  const templatePanelContentRef = useRef<TemplatePanelContentHandle>(null);

  // -- Electorate explanation dialog --
  const [isExplainElectoratesDialogOpen, setIsExplainElectoratesDialogOpen] = useState(false);

  // Called when the search tree state is updated by the SearchTreeBuilder in
  // the SearchTreeSection, as opposed to the user updating individual
  // placeholder descriptions etc, which is handled by PlaceholdersPanelContent
  const handleSearchTreeChange: SearchTreeChangeHandler = (
    newSearchTree,
    isIncomplete,
    actionMeta
  ) => {
    if (
      actionMeta?.type === 'SET_VALUE' &&
      compareElectionPlaceholders(searchTree, newSearchTree)
    ) {
      setIsExplainElectoratesDialogOpen(true);
    }

    setIsSearchTreeIncomplete(isIncomplete);
    setSearchTree(newSearchTree);

    setHasBeenModified(true);

    const isNotRenewing = hasNotRenewing(newSearchTree);
    setNotRenewingPresent(isNotRenewing);

    // Here we escape from the declarative pattern of React and make an
    // imperative call to the TemplatePanelContent to force it to sync its
    // placeholders to the new search tree. It won't clobber existing data the
    // user has already entered, it will only add new placeholders that are in
    // the tree but not in the panel, or remove ones that were in the panel but
    // are no longer in the tree
    if (templatePanelContentRef.current) {
      templatePanelContentRef.current.syncSearchTree(newSearchTree);
    } else {
      throw new InternalLogicError(
        'Unexpected state: templatePanelContentRef.current is null within call to handleSearchTreeChange()'
      );
    }
  };

  useEffect(() => {
    setIsNavbarOpen(false);
  }, [setIsNavbarOpen]);

  const hiddenPlaceholderKeys = useMemo(() => getHiddenPlaceholderKeys(searchTree), [searchTree]);

  const leafNodeIdsWithHiddenPlaceholders = useMemo(() => {
    const ids = new Set<string>();
    for (const key of hiddenPlaceholderKeys) {
      ids.add(key.split(':')[0]);
    }
    return ids;
  }, [hiddenPlaceholderKeys]);

  const [selectedPlaceholderKey, setSelectedPlaceholderKey] = useState<string | null>(null);

  const handlePlaceholderFocus = useCallback((placeholderKey: string) => {
    setSelectedPlaceholderKey(placeholderKey);
    const leafNodeId = placeholderKey.split(':')[0];
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-id="${leafNodeId}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, []);

  const handlePlaceholderBlur = useCallback(() => {
    setSelectedPlaceholderKey(null);
  }, []);

  const selectedPlaceholderLeafNodeId = selectedPlaceholderKey
    ? selectedPlaceholderKey.split(':')[0]
    : null;

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>List Manager - New Template</title>
        <meta name="description" content="List Manager - New Template" />
      </Helmet>
      <SearchTemplatePageContext value={true}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            height: "inherit",
            justifyContent: "space-between"
          }}>
          {enableDevFeatures && (
            <ToggleDebugButton
              debug={debug}
              setDebug={setDebug}
              sx={{
                position: 'absolute',
              }}
            />
          )}

          <Box
            tabIndex={0}
            sx={{
              flexGrow: 1,
              maxWidth: '2000px',
              padding: '2rem',
              overflow: "auto"
            }}>
            <SearchTreeBuilderSection
              templateNameToDisplay={templateNameToDisplay(loadedMetadata, hasBeenModified)}
              searchTreeBuilderState={searchTreeBuilderState}
              setSearchTreeBuilderState={setSearchTreeBuilderState}
              searchTree={searchTree}
              onSearchTreeChange={handleSearchTreeChange}
              leafNodeIdsWithHiddenPlaceholders={leafNodeIdsWithHiddenPlaceholders}
              selectedPlaceholderLeafNodeId={selectedPlaceholderLeafNodeId}
            />
          </Box>

          <Sidebar
            isOpen={true}
            title="Template builder"
            testId="rightSidePanel"
            position="right"
            noTopBorder
          >
            <TemplatePanelContent
              ref={templatePanelContentRef}
              initialMetadata={loadedMetadata}
              initialSearchTree={searchTree}
              searchTemplateId={searchTemplateId}
              searchTreeIsIncomplete={isSearchTreeIncomplete}
              hiddenPlaceholderKeys={hiddenPlaceholderKeys}
              onAnyChange={() => setHasBeenModified(true)}
              onTemplateSaveSuccess={handleTemplateSaveSuccess}
              onPlaceholderClick={handlePlaceholderFocus}
              onPlaceholderBlur={handlePlaceholderBlur}
              selectedPlaceholderKey={selectedPlaceholderKey}
            />
          </Sidebar>
        </Box>
      </SearchTemplatePageContext>
      <BlockerDialog blocker={blocker} popStateBlock={popStateBlock} />

      <AgDialog
        dialogTitle="Electorate selection"
        isOpen={isExplainElectoratesDialogOpen}
        primaryButton={{
          text: 'OK',
          onClick: async () => {
            setIsExplainElectoratesDialogOpen(false);
          },
        }}
        onClose={async () => {
          setIsExplainElectoratesDialogOpen(false);
        }}
        secondaryButton={{ text: '', disabled: true }}
      >
        {
          'When you set the Election field as a placeholder, the Electorates field is automatically set to "Any".'
        }
      </AgDialog>
    </>
  );
}
